import { quoteQuery } from "@common/api/quoting/quote"
import { quoteTaskCannedTasksQuery } from "@common/api/quoting/quote_task"
import { currentUserQuery } from "@common/api/users"
import type { QuoteTaskDetailPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@common/components/ui/dialog"
import { hasPermission } from "@common/lib/permissions"
import { plural } from "@common/lib/sentences"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import useCustomToast from "@/hooks/use-custom-toast"
import { applyTemplate } from "@/routes/_layout/quoting_/quotes/-lib/ops"
import {
  fromMatch,
  TemplateList,
  TemplatePreview,
  useSelectedTemplate,
} from "../../-components/template-picker"
import { useEditable } from "../../-lib/editable"
import { useQuoteUpdate } from "../../-lib/update"

function applyDescription(task: QuoteTaskDetailPublic): string {
  const charges = task.active_charges.length
  const steps = task.active_steps.length
  if (charges === 0 && steps === 0) {
    return `Copies the template's charges, steps, resolution and manual reference onto ${task.number}.`
  }
  return `Replaces ${task.number}'s ${plural(charges, "charge")} and ${plural(steps, "step")} with the template's, and copies its resolution and manual reference.`
}

const searchSchema = z.object({
  template: z.string().optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/quotes_/$quoteId/tasks/$taskId/apply-template",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, params }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (
      !hasPermission(me, "quote:update") ||
      !hasPermission(me, "canned_work_package:read")
    ) {
      throw redirect({ to: "/quotes/$quoteId/tasks/$taskId", params })
    }
  },
  component: ApplyTemplateDialog,
})

function ApplyTemplateDialog(): React.ReactElement | null {
  const { quoteId, taskId } = Route.useParams()
  const { template } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const { data: quote } = useSuspenseQuery(quoteQuery({ quote_id: quoteId }))
  const editable = useEditable(quote)
  const { update, pending } = useQuoteUpdate(quoteId)
  const { data: matches = [] } = useQuery({
    ...quoteTaskCannedTasksQuery({ quote_task_id: taskId }),
    retry: false,
  })
  const chosen = useSelectedTemplate(template)
  const task = quote.active_tasks.find((candidate) => candidate.id === taskId)

  const close = () =>
    navigate({
      to: "/quotes/$quoteId/tasks/$taskId",
      params: { quoteId, taskId },
      search: ({ template: _template, ...rest }) => rest,
    })
  const select = (id: string) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, template: id }),
      replace: true,
    })

  if (!task) return null

  const confirm = () =>
    chosen &&
    update(applyTemplate(task.id, chosen.id), () => {
      showToast(
        "Template applied",
        `${chosen.name} applied to ${task.number}.`,
        "success",
      )
      close()
    })

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Apply template</DialogTitle>
          <DialogDescription>{applyDescription(task)}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <TemplateList
            suggestions={matches.map(fromMatch)}
            selected={template}
            onSelect={select}
          />
          <TemplatePreview template={chosen} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!chosen || pending || !editable} onClick={confirm}>
            {pending ? "Applying…" : "Apply template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
